import React, { useState, useRef, useEffect, useContext } from "react";
import Link from "next/link";
import Head from "next/head";
import Image from "next/image";
import { GrRotateLeft } from "react-icons/gr";
import styles from "./type-signature.module.css";
import { toPng } from "html-to-image";
import { AiFillCheckCircle, AiOutlineClose } from "react-icons/ai";
import AppContext from "../../../components/AppContext";
import { createUTMQueryParams } from "@utils/signature_owner";

const fontNames = [
  styles.font115,
  styles.font2,
  styles.font91,
  styles.font92,
  styles.font3,
  styles.font116,
  styles.font117,
  styles.font118,
  styles.font119,
  styles.font4,
  styles.font1,
  styles.font5,
  styles.font6,
  styles.font7,
  styles.font8,
  styles.font9,
  styles.font10,
  styles.font11,
  styles.font12,
  styles.font13,
  styles.font14,
  styles.font15,
  styles.font16,
  styles.font17,
  styles.font18,
  styles.font19,
  styles.font20,
  styles.font21,
  styles.font22,
  styles.font23,
  styles.font24,
  styles.font25,
  styles.font26,
  styles.font27,
  styles.font28,
  styles.font29,
  styles.font30,
  styles.font31,
  styles.font32,
  styles.font33,
  styles.font34,
  styles.font35,
  styles.font36,
  styles.font37,
  styles.font38,
  styles.font39,
  styles.font40,
  styles.font41,
  styles.font42,
  styles.font43,
  styles.font44,
  styles.font45,
  styles.font46,
  styles.font47,
  styles.font48,
  styles.font49,
  styles.font50,
  styles.font51,
  styles.font52,
  styles.font53,
  styles.font54,
  styles.font55,
  styles.font56,
  styles.font57,
  styles.font58,
  styles.font59,
  styles.font60,
  styles.font61,
  styles.font62,
  styles.font63,
  styles.font64,
  styles.font65,
  styles.font66,
  styles.font67,
  styles.font68,
  styles.font69,
  styles.font70,
  styles.font71,
  styles.font72,
  styles.font73,
  styles.font74,
  styles.font75,
  styles.font76,
  styles.font77,
  styles.font78,
  styles.font79,
  styles.font80,
  styles.font81,
  styles.font82,
  styles.font83,
  styles.font84,
  styles.font85,
  styles.font86,
  styles.font87,
  styles.font88,
  styles.font89,
  styles.font90,
  styles.font93,
  styles.font94,
  styles.font95,
  styles.font96,
  styles.font97,
  styles.font98,
  styles.font99,
  styles.font100,
  styles.font101,
  styles.font103,
  styles.font105,
  styles.font106,
  styles.font107,
  styles.font108,
  styles.font109,
  styles.font110,
  styles.font111,
  styles.font112,
  styles.font113,
  styles.font114,
  styles.font120,
  styles.font121,
];

const TypeSignature = () => {
  const mainContent = useRef();
  const signatureBox = useRef();
  const context = useContext(AppContext);
  const [signatureText, setSignatureText] = useState("");
  const [penColor, setPenColor] = useState("#000000");
  const [showModal, setShowModal] = useState(false);
  const [currentFont, setCurrentFont] = useState(null);
  const [fontSize, setFontSize] = useState("25");
  const [downloadLoading, setDownloadLoading] = useState(false);

  async function handleDownload() {
    try {
      setDownloadLoading(true);
      if (signatureBox.current === null) {
        return;
      }
      const dataUrl = await toPng(signatureBox.current, { cacheBust: true });
      const link = document.createElement("a");
      link.download = "signature.png";
      link.href = dataUrl;
      link.click();
      setDownloadLoading(false);
    } catch (err) {
      setDownloadLoading(false);
      // console.log('Download Error: ' + err);
    }
  }

  const openModal = (font) => {
    if (signatureText === "") {
      return;
    }
    setShowModal(true);
    setCurrentFont(font);
    mainContent.current.style.display = "none";
  };

  const closeModal = () => {
    setShowModal(false);
    mainContent.current.style.display = "flex";
  };

  const increaseFontSize = () => {
    if (parseInt(fontSize) >= 100) {
      setFontSize(100);
      return;
    }
    setFontSize(parseInt(fontSize) + 5);
  };
  const decreaseFontSize = () => {
    if (parseInt(fontSize) <= 10) {
      setFontSize(10);
      return;
    }
    setFontSize(parseInt(fontSize) - 5);
  };

  return (
    <>
      <Head>
        <title>
          BoloForms Signature Tool - Type & Download Your eSignature
        </title>
        <meta
          name="description"
          content="BoloForms Signature Tool allows you to create your unique digital signature effortlessly. Type your signature and download it as a PNG file. Ideal for documents, contracts, and more."
        />
        <meta
          name="keywords"
          content="signature tool, signature generator, online signature, digital signature, e-signature, signature maker, signature creator, signature font, signature fonts, signature generator online, signature generator free, signature generator free online, signature generator free download, type signature, type your signature, type signature online, type signature generator, type signature font, type signature online free, type signature in word, type signature in pdf, type signature in google docs, type signature in google forms, type signature in google slides, type signature in google sheets, type signature in excel, type signature in outlook, type signature in gmail, type signature in word online, type signature in pdf online, type signature in google docs online, type signature in google forms online, type signature in google slides online, type signature in google sheets online, type signature in excel online, type signature in outlook online, type signature in gmail online"
        />
        <meta name="generator" content="BoloForms Signature Tool" />
        <meta
          property="og:title"
          content="BoloForms Signature Tool - Type & Download Your eSignature"
        />
        <meta
          property="og:description"
          content="BoloForms Signature Tool allows you to create your unique digital signature effortlessly. Type your signature and download it as a PNG file. Ideal for documents, contracts, and more."
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/online-signature/type/"
        />
        <meta property="og:type" content="website" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/online-signature/type/"
        />
      </Head>
      <div>
        <div
          ref={mainContent}
          className="flex flex-col max-w-[1280px] gap-[50px] mx-auto"
        >
          <div className="flex flex-col justify-center items-center text-center">
            {/* <div className="text-[20px] text-violet-800">Create and download your free e-signature</div> */}
            <div className="text-[35px] md:text-[50px] font-bold text-gray-800">
              Type your Signature
            </div>
            <div className="text-[20px] md:text-[25px] text-gray-600 mt-[20px] max-w-[900px]">
              An online signature generator/maker is a tool that helps you
              create an online signature. eSignatures are a fast and easy way to
              sign contracts and legal documents.
            </div>
          </div>
          <div className="flex flex-col gap-[20px] justify-center items-center w-full md:w-[600px] mx-auto">
            <input
              value={signatureText}
              onChange={(e) => setSignatureText(e.target.value)}
              className="border-[1px] border-gray-400 text-[25px] text-gray-500 px-[25px] py-[15px] h-[60px] w-full outline-none rounded-full"
              placeholder="Type your signature here"
              type="text"
              name=""
              id=""
            />
            <div className="flex items-center justify-center gap-[20px] text-gray-800">
              <div className="flex items-center gap-[5px] border border-gray-400 py-[2px] px-[5px] pl-[15px]  relative rounded-full">
                <input
                  type="color"
                  value={penColor}
                  onChange={(e) => setPenColor(e.target.value)}
                  name=""
                  id=""
                  className="border-none outline-none rounded-full w-[25px] h-[30px]"
                />
                <input
                  type="text"
                  value={penColor}
                  onChange={(e) => setPenColor(e.target.value)}
                  maxLength="7"
                  name=""
                  id=""
                  className="w-[90px] border-none outline-none bg-transparent"
                />
              </div>
              <button
                onClick={() => setSignatureText("")}
                className="flex items-center gap-[5px] border border-gray-400 py-[5px] px-[15px] relative rounded-full cursor-pointer hover:opacity-80"
              >
                <GrRotateLeft /> Clear
              </button>
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-[30px]">
            {signatureText &&
              fontNames.map((font, index) => (
                <div
                  key={index}
                  className="w-[320px] min-h-[300px] bg-white rounded-2xl p-[30px] border-[1px] border-gray-300 flex flex-col justify-between items-center gap-[30px]"
                >
                  <div
                    className={`text-center text-[35px] font-semibold w-full h-full flex-1 grid place-items-center ${font}`}
                    style={{ color: penColor }}
                  >
                    {signatureText}
                  </div>
                  <button
                    onClick={() => openModal(font)}
                    className="bg-violet-600 text-white w-full text-[17px] px-[20px] py-[8px] md:px-[40px] md:py-[12px] rounded-full hover:opacity-80"
                  >
                    Select Signature
                  </button>
                </div>
              ))}
          </div>
        </div>
        {showModal && (
          <div
            className={`absolute z-[1000] top-0 left-0 w-full flex justify-center items-start px-[5px] py-[100px] bg-white/50 min-h-screen`}
          >
            <div className="relative flex flex-col md:flex-row justify-center items-center w-fit bg-violet-500 mx-auto rounded-2xl">
              <div
                onClick={() => closeModal()}
                className="border border-b-none border-violet-300 shadow-xl absolute top-[-25px] left-[50%] -translate-x-[50%] w-[50px] h-[50px] hover:opacity-90 rounded-full cursor-pointer grid place-items-center bg-white text-violet-600 font-bold text-[20px]"
              >
                <AiOutlineClose className="font-bold" />
              </div>
              <div className="w-full lg:w-[500px] md:h-[550px] lg:h-[450px] text-white flex flex-col justify-between gap-[50px] md:gap-[10px] p-[50px] px-[20px] lg:px-[50px]">
                <div className="flex flex-col gap-[15px]">
                  <div className="text-[25px] font-semibold">
                    Download Your Signature
                  </div>
                  {/* <div className="text-[18px]">Go artistic. Use your mouse or trackpad to make a statement.</div> */}
                  <div
                    ref={signatureBox}
                    className="w-full sm:w-[400px] lg:w-full h-[150px] bg-white p-[10px] flex justify-center items-center rounded-lg relative"
                  >
                    <div
                      className={`font-[550] ${currentFont}`}
                      style={{ color: penColor, fontSize: fontSize + "px" }}
                    >
                      {signatureText}
                    </div>
                  </div>
                  <div className="flex items-center justify-start gap-[20px] text-gray-800">
                    <div className="flex bg-gray-200 items-center gap-[5px] border border-gray-400 py-[2px] px-[5px] pl-[15px]  relative rounded-full">
                      <input
                        type="color"
                        value={penColor}
                        onChange={(e) => setPenColor(e.target.value)}
                        name=""
                        id=""
                        className="border-none outline-none rounded-full w-[25px] h-[30px]"
                      />
                      <input
                        type="text"
                        value={penColor}
                        onChange={(e) => setPenColor(e.target.value)}
                        maxLength="7"
                        name=""
                        id=""
                        className="w-[90px] border-none outline-none bg-transparent"
                      />
                    </div>

                    <div className="flex flex-row w-[100px] h-[35px] rounded-full relative bg-transparent">
                      <button
                        onClick={() => decreaseFontSize()}
                        className=" bg-gray-300 text-gray-600 hover:text-gray-700 hover:bg-gray-400 h-full w-20 rounded-l-full cursor-pointer outline-none"
                      >
                        <span className="m-auto text-2xl font-thin">−</span>
                      </button>
                      <input
                        value={fontSize}
                        readOnly
                        type="text"
                        className="focus:outline-none text-center w-full bg-gray-200 font-semibold text-md hover:text-black focus:text-black  md:text-basecursor-default flex items-center text-gray-700  outline-none"
                        name=""
                      />
                      <button
                        onClick={() => increaseFontSize()}
                        className="bg-gray-300 text-gray-600 hover:text-gray-700 hover:bg-gray-400 h-full w-20 rounded-r-full cursor-pointer"
                      >
                        <span className="m-auto text-2xl font-thin">+</span>
                      </button>
                    </div>
                  </div>
                  {downloadLoading ? (
                    <div
                      class={`ml-[20px] flex items-center text-white space-x-2 ${styles.loader}`}
                    >
                      <div class="">Downloading</div>
                      <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                      <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                      <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    </div>
                  ) : (
                    <div className="text-white ml-[10px]">
                      Download your signature as a PNG file.
                    </div>
                  )}
                </div>
                <div className="">
                  <button
                    onClick={() => handleDownload()}
                    className="bg-white text-violet-600 text-[17px] md:text-[20px] px-[20px] py-[8px] md:px-[40px] md:py-[12px] rounded-full hover:opacity-80"
                  >
                    Download your signature
                  </button>
                </div>
              </div>
              <div className="w-[90%] h-[1px] md:w-[1px] md:h-[350px] bg-white"></div>
              <div className="lg:w-[500px] md:h-[500px] lg:h-[400px] text-white flex flex-col justify-between gap-[50px] md:gap-[10px] p-[50px] px-[20px] lg:px-[50px]">
                <div className="flex flex-col gap-[15px]">
                  <div className="text-[25px] font-semibold">
                    Take Signatures inside Forms!
                  </div>
                  <div className="text-[18px]">Get started for Free</div>
                  <div className="">
                    {[
                      "Integrated Signatures: Add digital signature fields to your forms for easy signing.",
                      "PDF Downloads: Download documents with signatures as PDF files.",
                      "Super easy and simple to use.",
                    ].map((item, index) => {
                      return (
                        <div key={index} className="flex gap-[10px] text-white">
                          <div className="">
                            <AiFillCheckCircle className="text-[20px]" />
                          </div>
                          {item}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="">
                  <Link
                    target="_blank"
                    href={`https://signature.boloforms.com/login?from=${
                      context.globalData.from || "ONLINE_SIGNATURES_TYPE"
                    }${createUTMQueryParams({
                      utmData_: context.globalData.utmData,
                      from: "ONLINE_SIGNATURES_TYPE",
                    })}`}
                    className="bg-white text-violet-600 text-[17px] md:text-[20px] px-[20px] py-[8px] md:px-[40px] md:py-[12px] rounded-full hover:opacity-80"
                  >
                    Check out BoloForms
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TypeSignature;
